import type {
  BuilderElement,
  ElementType,
  ResponsiveValue,
} from '@/utils/builder/types';
import { generateId } from '@/utils/builder/id-utils';

// Check if logo is responsive (object with breakpoint keys)
export const isResponsiveLogo = (
  logo: unknown
): logo is ResponsiveValue<string> => {
  return logo !== null && typeof logo === 'object' && 'desktop' in logo;
};

// Get logo URL for a specific breakpoint
export const getLogoForBreakpoint = (
  logo: string | ResponsiveValue<string> | undefined,
  breakpoint: 'desktop' | 'tablet' | 'mobile'
): string | undefined => {
  if (!logo) return undefined;
  if (typeof logo === 'string') return logo;
  return logo[breakpoint] || logo.desktop;
};

// Normalize logo to responsive format
export const normalizeLogoConfig = (
  logo: string | ResponsiveValue<string> | undefined
): ResponsiveValue<string> | undefined => {
  if (!logo) return undefined;
  if (typeof logo === 'string') {
    return { desktop: logo, tablet: logo, mobile: logo };
  }

  const firstAvailableLogo = logo.desktop || logo.tablet || logo.mobile;
  if (!firstAvailableLogo) return undefined;

  return {
    desktop: logo.desktop || firstAvailableLogo,
    tablet: logo.tablet || logo.desktop || firstAvailableLogo,
    mobile: logo.mobile || logo.desktop || firstAvailableLogo,
  };
};

// Apply logo to all logo elements in template recursively
export const applyLogoToTemplate = (
  elements: BuilderElement[],
  logo?: string | ResponsiveValue<string>,
  businessName?: string
): BuilderElement[] => {
  return elements.map((element) => {
    if (element.type === 'logo') {
      const logoConfig = normalizeLogoConfig(logo);

      if (logoConfig?.desktop && logoConfig.desktop.trim() !== '') {
        const newSrcResponsive = {
          desktop: logoConfig.desktop,
          tablet: logoConfig.tablet || logoConfig.desktop,
          mobile: logoConfig.mobile || logoConfig.desktop,
        };

        return {
          ...element,
          children: [
            {
              id: generateId(),
              type: 'image' as ElementType,
              src: logoConfig.desktop,
              srcResponsive: newSrcResponsive,
              alt: businessName || 'Logo',
              styles: {
                height: 'auto',
                maxHeight: '40px',
                width: 'auto',
                objectFit: 'contain',
              },
            },
          ],
        };
      }
      return { ...element, children: element.children || [] };
    }

    if (element.children) {
      return {
        ...element,
        children: applyLogoToTemplate(element.children, logo, businessName),
      };
    }

    return element;
  });
};
